if (!customElements.get('three-cubes-reading-progress')) {
  class ThreeCubesReadingProgress extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;

      this.initialized = true;
      this.bar = this.querySelector('span');
      this.frame = null;

      if (!this.bar) return;

      this.handleViewportChange = () => {
        if (this.frame !== null) return;

        this.frame = window.requestAnimationFrame(() => {
          this.frame = null;
          this.updateProgress();
        });
      };

      window.addEventListener('scroll', this.handleViewportChange, { passive: true });
      window.addEventListener('resize', this.handleViewportChange, { passive: true });
      this.updateProgress();
    }

    disconnectedCallback() {
      if (!this.handleViewportChange) return;

      window.removeEventListener('scroll', this.handleViewportChange);
      window.removeEventListener('resize', this.handleViewportChange);

      if (this.frame !== null) window.cancelAnimationFrame(this.frame);
    }

    updateProgress() {
      const scrollingElement = document.scrollingElement || document.documentElement;
      const startSelector = this.dataset.progressStart;
      const endSelector = this.dataset.progressEnd;

      if (!this.startTarget?.isConnected) {
        this.startTarget = startSelector ? document.querySelector(startSelector) : null;
      }

      if (!this.endTarget?.isConnected) {
        this.endTarget = endSelector ? document.querySelector(endSelector) : null;
      }

      if (!this.startTarget || !this.endTarget) {
        this.bar.style.transform = 'scaleX(0)';
        return;
      }

      const scrollTop = scrollingElement.scrollTop;
      const startOffset = this.startTarget.getBoundingClientRect().top + scrollTop;
      const endOffset = this.endTarget.getBoundingClientRect().bottom + scrollTop - window.innerHeight;
      const readingDistance = endOffset - startOffset;
      const progress = readingDistance > 0
        ? (scrollTop - startOffset) / readingDistance
        : Number(scrollTop >= startOffset);

      this.bar.style.transform = `scaleX(${Math.min(1, Math.max(0, progress))})`;
    }
  }

  customElements.define('three-cubes-reading-progress', ThreeCubesReadingProgress);
}

if (!customElements.get('three-cubes-section')) {
  class ThreeCubesSection extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;

      this.initialized = true;
      this.revealObserver = null;
      this.woodObserver = null;
      this.captionTimer = null;
      this.reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.designMode = Boolean(window.Shopify && window.Shopify.designMode);

      this.setupReveals();
      this.setupWoodStory();
      this.classList.add('is-ready');
    }

    disconnectedCallback() {
      this.revealObserver?.disconnect();
      this.woodObserver?.disconnect();
      if (this.captionTimer !== null) window.clearTimeout(this.captionTimer);
    }

    setupReveals() {
      const elements = [...this.querySelectorAll('.tc-reveal')];

      if (
        elements.length === 0 ||
        this.reduceMotion ||
        this.designMode ||
        !('IntersectionObserver' in window)
      ) {
        elements.forEach((element) => element.classList.add('is-visible'));
        return;
      }

      this.revealObserver = new IntersectionObserver(
        (entries, observer) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;

            entry.target.classList.add('is-visible');
            observer.unobserve(entry.target);
          });
        },
        {
          rootMargin: '0px 0px -10% 0px',
          threshold: 0.12,
        },
      );

      elements.forEach((element) => this.revealObserver.observe(element));
    }

    setupWoodStory() {
      const chapters = [...this.querySelectorAll('[data-wood]')];
      const images = [...this.querySelectorAll('[data-wood-image]')];
      const currentIndex = this.querySelector('[data-current-index]');
      const currentName = this.querySelector('[data-current-name]');
      const currentMeaning = this.querySelector('[data-current-meaning]');
      const caption = this.querySelector('.tc-wood-story__caption');

      if (chapters.length === 0 || images.length === 0) return;

      let activeChapter = null;

      const updateCaption = (chapter) => {
        if (currentIndex) currentIndex.textContent = chapter.dataset.index || '';
        if (currentName) currentName.textContent = chapter.dataset.name || '';
        if (currentMeaning) currentMeaning.textContent = chapter.dataset.meaning || '';
      };

      const activate = (chapter, instant = false) => {
        if (!chapter || chapter === activeChapter) return;

        const wood = chapter.dataset.wood;
        activeChapter = chapter;

        chapters.forEach((item) => item.classList.toggle('is-active', item === chapter));
        images.forEach((image) => image.classList.toggle('is-active', image.dataset.woodImage === wood));

        if (this.captionTimer !== null) window.clearTimeout(this.captionTimer);

        if (instant || !caption) {
          updateCaption(chapter);
          caption?.classList.remove('is-changing');
          this.captionTimer = null;
          return;
        }

        if (!this.reduceMotion) caption.classList.add('is-changing');
        this.captionTimer = window.setTimeout(() => {
          updateCaption(chapter);
          caption.classList.remove('is-changing');
          this.captionTimer = null;
        }, this.reduceMotion ? 180 : 290);
      };

      activate(chapters[0], true);

      if (!('IntersectionObserver' in window)) return;

      this.woodObserver = new IntersectionObserver(
        (entries) => {
          const visibleEntry = entries
            .filter((entry) => entry.isIntersecting)
            .sort((a, b) => b.intersectionRatio - a.intersectionRatio)[0];

          if (visibleEntry) activate(visibleEntry.target);
        },
        {
          rootMargin: '-28% 0px -48% 0px',
          threshold: [0, 0.15, 0.4, 0.65],
        },
      );

      chapters.forEach((chapter) => this.woodObserver.observe(chapter));
    }
  }

  customElements.define('three-cubes-section', ThreeCubesSection);
}
